/**
 * The form a manifest describes.
 *
 * This is the whole promise of the platform: a PM fills this in instead of asking an
 * engineer to run something. So it does the fiddly parts properly — files upload
 * straight to S3, per-field errors from the API land on the right control, and the
 * submit button says what will actually happen.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { endpoints, uploadToS3 } from "@/api/endpoints";
import { useAsync } from "@/hooks/useAsync";
import type { InputSpec, Manifest, ParamSpec } from "./manifest";
import { checkFile, initialValues, labelFor, prepareParams, validate } from "./manifest";
import { ParamField } from "./ParamField";
import { Field, Notice } from "./ui";

type Phase = "editing" | "creating" | "uploading" | "starting";

interface Props {
  projectSlug: string;
  scriptName: string;
  manifest: Manifest;
  isPii: boolean;
}

function FileInput({
  spec,
  files,
  error,
  disabled,
  onChange,
}: {
  spec: InputSpec;
  files: File[];
  error?: string;
  disabled: boolean;
  onChange: (files: File[]) => void;
}) {
  const id = `input-${spec.name}`;
  const limits = [
    spec.accept?.length ? spec.accept.join(", ") : undefined,
    spec.max_size_mb ? `up to ${spec.max_size_mb} MB` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Field
      label={labelFor(spec)}
      hint={[spec.description, limits].filter(Boolean).join(" — ") || undefined}
      error={error}
      required={spec.required}
      htmlFor={id}
    >
      <input
        id={id}
        className="input"
        type="file"
        multiple={spec.multiple}
        accept={spec.accept?.join(",")}
        disabled={disabled}
        onChange={(event) => onChange(Array.from(event.target.files ?? []))}
      />
      {files.length > 0 && (
        <div className="faint" style={{ fontSize: 12.5 }}>
          {files.map((file) => file.name).join(", ")}
        </div>
      )}
    </Field>
  );
}

export function RunForm({ projectSlug, scriptName, manifest, isPii }: Props) {
  const navigate = useNavigate();

  const params = useMemo<ParamSpec[]>(() => manifest.params ?? [], [manifest]);
  const inputs = useMemo<InputSpec[]>(() => manifest.inputs ?? [], [manifest]);
  const hasSecretRef = useMemo(() => params.some((spec) => spec.type === "secret_ref"), [params]);
  const { data: secrets } = useAsync(() => endpoints.secrets(projectSlug), [projectSlug], {
    enabled: hasSecretRef,
  });
  const secretNames = useMemo(() => (secrets ?? []).map((s) => s.name), [secrets]);

  const [values, setValues] = useState<Record<string, unknown>>(() => initialValues(params));
  const [files, setFiles] = useState<Record<string, File[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string>();
  const [phase, setPhase] = useState<Phase>("editing");
  const [progress, setProgress] = useState<string>();

  const busy = phase !== "editing";

  const setValue = (name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors(({ [name]: _removed, ...rest }) => rest);
  };

  const setInputFiles = (spec: InputSpec, chosen: File[]) => {
    const rejected = chosen.map((file) => checkFile(spec, file)).find(Boolean);
    setErrors((current) => {
      const next = { ...current };
      if (rejected) next[spec.name] = rejected;
      else delete next[spec.name];
      return next;
    });
    setFiles((current) => ({ ...current, [spec.name]: chosen }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setProblem(undefined);

    const found = validate(params, values);
    for (const spec of inputs) {
      if (spec.required && !(files[spec.name] ?? []).length) {
        found[spec.name] = "Choose a file.";
      }
    }
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }

    try {
      setPhase("creating");
      const selection = Object.fromEntries(
        Object.entries(files)
          .filter(([, chosen]) => chosen.length > 0)
          .map(([name, chosen]) => [
            name,
            chosen.map((file) => ({ filename: file.name, size_bytes: file.size })),
          ]),
      );

      const created = await endpoints.createRun(projectSlug, scriptName, {
        params: prepareParams(params, values),
        inputs: selection,
      });

      // The bytes go straight to S3 — the API never carries them, which is what makes a
      // multi-gigabyte export practical.
      const uploads = created.uploads ?? [];
      if (uploads.length > 0) {
        setPhase("uploading");
        const byName = new Map<string, File>();
        for (const [name, chosen] of Object.entries(files)) {
          for (const file of chosen) byName.set(`${name}:${file.name}`, file);
        }

        let done = 0;
        for (const target of uploads) {
          const file = byName.get(`${target.input_name}:${target.filename}`);
          if (!file) continue;
          setProgress(`Uploading ${target.filename} (${++done} of ${uploads.length})`);
          await uploadToS3(target, file);
        }
      }

      setPhase("starting");
      setProgress("Starting the run");
      await endpoints.startRun(projectSlug, created.run.id);

      navigate(`/projects/${projectSlug}/runs/${created.run.id}`);
    } catch (caught) {
      setPhase("editing");
      setProgress(undefined);
      if (caught instanceof ApiError) {
        const fields = Object.fromEntries(
          Object.entries(caught.fields).map(([name, messages]) => [name, messages.join(" ")]),
        );
        setErrors(fields);
        setProblem(Object.keys(fields).length > 0 ? undefined : caught.message);
      } else {
        setProblem((caught as Error).message);
      }
    }
  };

  const timeout = manifest.runtime?.resources?.timeout_minutes;

  return (
    <form className="card" onSubmit={submit} noValidate>
      <div className="card__header">
        <span className="card__title">Run this script</span>
        {timeout && <span className="muted" style={{ fontSize: 12.5 }}>Times out after {timeout} min</span>}
      </div>

      <div className="card__body stack" style={{ gap: 18 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        {isPii && (
          <Notice tone="warning">
            This project holds customer data. Downloading anything this run produces will
            ask you for a reason, recorded against your name.
          </Notice>
        )}

        {inputs.length > 0 && (
          <div className="stack" style={{ gap: 14 }}>
            <h3>Files</h3>
            {inputs.map((spec) => (
              <FileInput
                key={spec.name}
                spec={spec}
                files={files[spec.name] ?? []}
                error={errors[spec.name]}
                disabled={busy}
                onChange={(chosen) => setInputFiles(spec, chosen)}
              />
            ))}
          </div>
        )}

        {params.length > 0 && (
          <div className="stack" style={{ gap: 14 }}>
            {inputs.length > 0 && <h3>Options</h3>}
            {params.map((spec) => (
              <ParamField
                key={spec.name}
                spec={spec}
                value={values[spec.name]}
                error={errors[spec.name]}
                disabled={busy}
                secrets={secretNames}
                onChange={(value) => setValue(spec.name, value)}
              />
            ))}
          </div>
        )}

        {params.length === 0 && inputs.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            This script takes no parameters or files.
          </p>
        )}

        <div className="row" style={{ gap: 12 }}>
          <button type="submit" className="button button--primary" disabled={busy}>
            {phase === "editing" ? "Run script" : "Working…"}
          </button>
          {progress && <span className="muted">{progress}</span>}
        </div>
      </div>
    </form>
  );
}
